/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { useWindowSize } from 'react-use';
import Confetti from 'react-confetti';
import WelcomeScreen from './WelcomeScreen.tsx';
import { auth, db } from '../../firebase.js';

interface UserData {
  email?: string;
  firstname?: string;
  lastname?: string;
  name?: string;
}

interface EmailVerificationHandlerProps {
  authReady: boolean;
  onComplete: () => void;
  onWelcomeVisible?: (visible: boolean) => void;
}

const EmailVerificationHandler: React.FC<EmailVerificationHandlerProps> = ({ 
  authReady, 
  onComplete,
  onWelcomeVisible
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { width, height } = useWindowSize();
  
  const [showWelcome, setShowWelcome] = useState(false);
  const [currentUserData, setCurrentUserData] = useState<UserData | null>(null);
  const [emailVerificationProcessed, setEmailVerificationProcessed] = useState(false);

  /**
   * Shows the welcome screen and notifies the parent component via the
   * optional onWelcomeVisible callback. http://localhost:3000/?preview=welcome
   *
   * Wrapped in useCallback so its reference stays stable across renders.
   * This prevents the useEffect below from re-firing every render just
   * because this function was recreated.
   */
  const showWelcomeScreen = useCallback(() => {
    setShowWelcome(true);
    onWelcomeVisible?.(true);
  }, [onWelcomeVisible]); // Re-create only if the parent callback changes

  /**
   * Handles the full email verification flow:
   * - If logged in: updates Firestore, stores user data, sends admin notification email
   * - If NOT logged in: shows a generic welcome message
   * - On error: still shows the welcome screen so the user isn't stuck
   *
   * Wrapped in useCallback so the useEffect dependency array remains valid
   * without causing an infinite re-run loop.
   */
  const handleEmailVerification = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      console.log('Current user in handleEmailVerification:', currentUser);
      
      if (currentUser) {
        // User is logged in — fetch their Firestore document
        const userRef = doc(db, 'users', currentUser.uid);
        
        // Mark the user as email-verified in Firestore
        await updateDoc(userRef, { emailVerified: true });
        
        // Fetch full user data so we can display their name on the welcome screen
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        const fullName = `${userData?.firstname || ''} ${userData?.lastname || ''}`.trim();
        
        // Store user data in state for WelcomeScreen
        setCurrentUserData({
          email: currentUser.email || undefined,
          firstname: userData?.firstname,
          lastname: userData?.lastname,
          name: fullName
        });
        
        // Show welcome screen immediately — don't wait for the admin email below
        showWelcomeScreen();
        
        // Notify admin that a new user has verified their email
        await fetch(`${API_BASE_URL}/.netlify/functions/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: 'sherpas@merch-sherpa.com',
            subject: '🎉 New User Verified Email',
            text: `A new user has verified their email!\n\nName: ${fullName}\nEmail: ${currentUser.email}\nUser ID: ${currentUser.uid}\nVerified at: ${new Date().toLocaleString()}`,
            html: `
              <div style="font-family: Arial, sans-serif; padding: 20px;">
                <h2 style="color: #28a745;">🎉 New User Verified!</h2>
                <p>A new user has successfully verified their email and joined your platform.</p>
                <table style="border-collapse: collapse; margin: 20px 0;">
                  <tr>
                    <td style="padding: 8px; font-weight: bold;">Name:</td>
                    <td style="padding: 8px;">${fullName}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px; font-weight: bold;">Email:</td>
                    <td style="padding: 8px;">${currentUser.email}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px; font-weight: bold;">User ID:</td>
                    <td style="padding: 8px;">${currentUser.uid}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px; font-weight: bold;">Verified at:</td>
                    <td style="padding: 8px;">${new Date().toLocaleString()}</td>
                  </tr>
                </table>
              </div>
            `
          })
        });
        
        console.log('IN EMAIL VERIFICATION HANDLER - Email verification processed successfully');
      } else {
        // User is NOT logged in — show a generic welcome rather than erroring out
        console.log('User not logged in, showing generic welcome');
        setCurrentUserData({
          email: 'New User',
          firstname: 'New User',
          name: 'New User'
        });
        showWelcomeScreen();
      }
    } catch (error) {
      console.error('Error processing email verification:', error);
      // Still show the welcome screen on error so the user isn't stuck on a blank page
      setCurrentUserData({
        email: 'User',
        firstname: 'User',
        name: 'User'
      });
      showWelcomeScreen();
    }
  }, [showWelcomeScreen]); // Re-create only if showWelcomeScreen's reference changes

  /**
   * Watches for email verification query params in the URL and kicks off
   * the verification flow when all conditions are met.
   *
   * Also handles the DEV PREVIEW mode (?preview=welcome) for testing the
   * welcome screen without going through the full auth flow.
   */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // DEV PREVIEW: ?preview=welcome bypasses auth and shows the welcome screen directly
    if (params.get('preview') === 'welcome') {
      setCurrentUserData({
        email: 'preview@example.com',
        firstname: 'Preview',
        name: 'Preview User'
      });
      showWelcomeScreen();
      return;
    }

    // Only run once per mount: require the emailVerified param, auth to be ready,
    // and the processed flag to be false so we don't fire this twice
    if (params.get('emailVerified') === 'true' && authReady && !emailVerificationProcessed) {
      setEmailVerificationProcessed(true);
      handleEmailVerification();
      
      // Clean up the emailVerified param from the URL after 5 seconds
      const timer = setTimeout(() => {
        params.delete('emailVerified');
        navigate({ search: params.toString() }, { replace: true });
      }, 5000);
      
      // Clear the timer if the component unmounts before the 5 seconds are up
      return () => clearTimeout(timer);
    }
  }, [location.search, navigate, authReady, emailVerificationProcessed, handleEmailVerification, showWelcomeScreen]);

  /**
   * Called when the user clicks "Continue" on the WelcomeScreen.
   * Hides the welcome screen and notifies the parent that the flow is complete.
   */
  const handleContinue = () => {
    setShowWelcome(false);
    onWelcomeVisible?.(false);
    onComplete();
  };

  // Render nothing until the welcome screen is triggered
  if (!showWelcome) {
    return null;
  }

  return (
    <div id="welcome-user">
      <Confetti width={width} height={height} />
      <WelcomeScreen 
        userEmail={currentUserData?.email} 
        userName={currentUserData?.firstname || currentUserData?.name}
        onContinue={handleContinue}
      />
    </div>
  );
};

export default EmailVerificationHandler;
